import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import "./FileUpload.css";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_PDF_PAGES = 20;

const FileUpload = ({ onFileUpload, onValidationError }) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fileType, setFileType] = useState(null);

  const resetSelection = () => {
    setFiles([]);
    setPreviews([]);
    setCurrentIndex(0);
    setFileType(null);
    onFileUpload([]);
  };

  const notifyError = (message) => {
    if (onValidationError) {
      onValidationError(message);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/jpg": [],
      "image/png": [],
      "application/pdf": [],
    },
    multiple: true,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        notifyError("Upload only PDF, JPG, JPEG, or PNG files.");
        resetSelection();
        return;
      }

      const oversizedFile = acceptedFiles.find((file) => file.size > MAX_FILE_SIZE_BYTES);
      if (oversizedFile) {
        notifyError(`"${oversizedFile.name}" is larger than 5 MB. Please upload a smaller file.`);
        resetSelection();
        return;
      }

      const imageFiles = acceptedFiles.filter((file) => file.type.startsWith("image/"));
      const pdfFiles = acceptedFiles.filter((file) => file.type === "application/pdf");

      notifyError("");

      if (imageFiles.length > 0) {
        const uploadedFiles = imageFiles.slice(0, 50);
        setFiles(uploadedFiles);
        setFileType("image");
        const newPreviews = uploadedFiles.map((file) => URL.createObjectURL(file));
        setPreviews(newPreviews);
        setCurrentIndex(0);
        onFileUpload(uploadedFiles);
      } else if (pdfFiles.length > 0) {
        const pdfFile = pdfFiles[0];
        setFiles([pdfFile]);
        setFileType("pdf");
        await renderPdfForPreview(pdfFile);
        setCurrentIndex(0);
        onFileUpload([pdfFile]);
      }
    },
  });

  const renderPdfForPreview = async (pdfFile) => {
    const fileReader = new FileReader();
    fileReader.onload = async function (e) {
      const typedArray = new Uint8Array(e.target.result);
      try {
        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
        const totalPages = pdf.numPages;
        if (totalPages > MAX_PDF_PAGES) {
          notifyError(`PDF files can have at most ${MAX_PDF_PAGES} pages. This file has ${totalPages} pages.`);
          resetSelection();
          return;
        }
        const renderedPages = [];
        for (let i = 1; i <= totalPages; i++) {
          const dataURL = await renderPdfPageToImage(pdf, i);
          renderedPages.push(dataURL);
        }
        setPreviews(renderedPages);
      } catch (error) {
        console.error("Error reading PDF:", error);
        notifyError("Failed to read PDF preview.");
      }
    };
    fileReader.readAsArrayBuffer(pdfFile);
  };

  const renderPdfPageToImage = async (pdf, pageNumber) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };
    await page.render(renderContext).promise;
    return canvas.toDataURL("image/jpeg");
  };

  const removeCurrentFile = () => {
    if (fileType === "image" && files.length > 0) {
      const updatedFiles = [...files];
      const updatedPreviews = [...previews];
      updatedFiles.splice(currentIndex, 1);
      updatedPreviews.splice(currentIndex, 1);
      setFiles(updatedFiles);
      setPreviews(updatedPreviews);
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
      onFileUpload(updatedFiles);
    } else if (fileType === "pdf" && files.length > 0) {
      resetSelection();
    }
  };

  const nextFile = () => {
    if (currentIndex < previews.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const previousFile = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const renderPreview = () => {
    if (previews.length === 0) return null;

    const currentPreview = previews[currentIndex];
    const totalItems = previews.length;

    return (
      <div className="file-preview-wrapper">
        <div className="file-preview">
          <img
            src={currentPreview}
            alt={`Preview ${currentIndex}`}
            className="preview-image"
          />
          <p className="image-count">{`Showing ${currentIndex + 1} of ${totalItems} ${fileType === "image" ? "images" : "pages"}`}</p>
          <div className="button-group">
            <button onClick={previousFile} disabled={currentIndex === 0} className="nav-button left">
              Previous
            </button>
            <button onClick={removeCurrentFile} className="remove-button center">
              Remove
            </button>
            <button
              onClick={nextFile}
              disabled={currentIndex === totalItems - 1}
              className="nav-button right"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      className="file-upload-container"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {files.length === 0 && (
        <div {...getRootProps({ className: `dropzone ${isDragActive ? "drag-active" : ""}` })}>
          <input {...getInputProps()} />
          <p>{isDragActive ? "Drop the file to upload" : "Drag and drop files here"}</p>
          <span className="dropzone-hint">PDF, JPG, JPEG, PNG. Max 5 MB per file. PDFs up to 20 pages.</span>
        </div>
      )}
      {renderPreview()}
    </motion.div>
  );
};

export default FileUpload;
